import React, { useState } from 'react';
import { useApp, UNITS, CATEGORIES } from '../contexts/AppContext';
import { Plus, Trash2, Edit2, X, Check, Database, Search, Download, Printer, TrendingUp, TrendingDown, Minus, History } from 'lucide-react';
import { exportIngredientsCSV, printIngredientsReport } from '../utils/exportUtils';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const EMPTY_INGREDIENT = {
  name: '', vendor: '', category: 'Other',
  purchaseCost: 0, purchaseQuantity: 1, purchaseUnit: 'lb',
  yieldPercent: 100, notes: '',
};

const formatCurrency = (val) => `$${(val || 0).toFixed(4)}`;

export default function IngredientsPage() {
  const { ingredients, addIngredient, updateIngredient, deleteIngredient } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_INGREDIENT });
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [priceHistoryId, setPriceHistoryId] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    const data = {
      ...form,
      purchaseCost: Number(form.purchaseCost) || 0,
      purchaseQuantity: Number(form.purchaseQuantity) || 1,
      yieldPercent: Number(form.yieldPercent) || 100,
    };

    if (editId) {
      updateIngredient(editId, data);
      setEditId(null);
    } else {
      addIngredient(data);
    }
    setForm({ ...EMPTY_INGREDIENT });
    setShowForm(false);
  };

  const startEdit = (ing) => {
    setForm({
      name: ing.name,
      vendor: ing.vendor,
      category: ing.category,
      purchaseCost: ing.purchaseCost,
      purchaseQuantity: ing.purchaseQuantity,
      purchaseUnit: ing.purchaseUnit,
      yieldPercent: ing.yieldPercent,
      notes: ing.notes || '',
    });
    setEditId(ing.id);
    setShowForm(true);
  };

  const filtered = ingredients.filter(ing => {
    const matchSearch = !search || ing.name.toLowerCase().includes(search.toLowerCase()) || ing.vendor.toLowerCase().includes(search.toLowerCase());
    const matchCategory = filterCategory === 'all' || ing.category === filterCategory;
    return matchSearch && matchCategory;
  });

  return (
    <div className="page-enter p-4 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#E23D28] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(42,42,42,1)]">
            <Database className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-4xl sm:text-5xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="ingredients-title">
              Ingredient Database
            </h1>
            <p className="text-sm text-[#2A2A2A]/60">{ingredients.length} ingredients</p>
          </div>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditId(null); setForm({ ...EMPTY_INGREDIENT }); }}
          className="pizza-btn text-sm flex items-center gap-2"
          data-testid="add-ingredient-btn"
        >
          <Plus className="w-4 h-4" /> Add Ingredient
        </button>
      </div>

      {/* Export Bar */}
      {ingredients.length > 0 && (
        <div className="flex gap-2 mb-4" data-testid="ingredients-export-bar">
          <button onClick={() => exportIngredientsCSV(ingredients)} className="pizza-btn-secondary text-xs px-4 py-1.5 flex items-center gap-1" data-testid="ingredients-export-csv-btn">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button onClick={() => printIngredientsReport(ingredients)} className="pizza-btn text-xs px-4 py-1.5 flex items-center gap-1" data-testid="ingredients-print-btn">
            <Printer className="w-3.5 h-3.5" /> Print / PDF
          </button>
        </div>
      )}

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2A2A2A]/40" />
          <input
            type="text"
            placeholder="Search ingredients..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pizza-input w-full pl-10"
            data-testid="ingredients-search"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-full sm:w-48 border-2 border-[#2A2A2A] rounded-lg" data-testid="ingredients-category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="pizza-card p-6 mb-6" data-testid="ingredient-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              {editId ? 'Edit Ingredient' : 'New Ingredient'}
            </h2>
            <button onClick={() => { setShowForm(false); setEditId(null); }} className="text-[#2A2A2A]/50 hover:text-[#E23D28]">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Name *</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="pizza-input w-full" required data-testid="ingredient-name-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Vendor</label>
              <input type="text" value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))}
                className="pizza-input w-full" data-testid="ingredient-vendor-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Category</label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="border-2 border-[#2A2A2A] rounded-lg w-full" data-testid="ingredient-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Purchase Cost ($)</label>
              <input type="number" step="0.01" min="0" value={form.purchaseCost}
                onChange={e => setForm(f => ({ ...f, purchaseCost: e.target.value }))}
                className="pizza-input w-full" data-testid="ingredient-cost-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Purchase Quantity</label>
              <input type="number" step="0.01" min="0.01" value={form.purchaseQuantity}
                onChange={e => setForm(f => ({ ...f, purchaseQuantity: e.target.value }))}
                className="pizza-input w-full" data-testid="ingredient-quantity-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Purchase Unit</label>
              <Select value={form.purchaseUnit} onValueChange={v => setForm(f => ({ ...f, purchaseUnit: v }))}>
                <SelectTrigger className="border-2 border-[#2A2A2A] rounded-lg w-full" data-testid="ingredient-unit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Usable Yield %</label>
              <input type="number" step="1" min="1" max="100" value={form.yieldPercent}
                onChange={e => setForm(f => ({ ...f, yieldPercent: e.target.value }))}
                className="pizza-input w-full" data-testid="ingredient-yield-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Notes</label>
              <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="pizza-input w-full" data-testid="ingredient-notes-input" />
            </div>
            <div className="flex items-end gap-2">
              <button type="submit" className="pizza-btn text-sm flex items-center gap-1" data-testid="ingredient-submit-btn">
                <Check className="w-4 h-4" /> {editId ? 'Update' : 'Add'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditId(null); }}
                className="pizza-btn-secondary text-sm" data-testid="ingredient-cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      {filtered.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="pizza-table" data-testid="ingredients-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Vendor</th>
                <th>Category</th>
                <th>Cost</th>
                <th>Trend</th>
                <th>Qty</th>
                <th>Unit</th>
                <th>Yield%</th>
                <th>Cost/Usable Unit</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(ing => {
                const history = ing.priceHistory || [];
                const hasHistory = history.length > 1;
                const lastPrice = hasHistory ? history[history.length - 2].cost : null;
                const currentPrice = ing.purchaseCost || 0;
                const trend = lastPrice !== null ? (currentPrice > lastPrice ? 'up' : currentPrice < lastPrice ? 'down' : 'stable') : 'stable';
                const pctChange = lastPrice && lastPrice > 0 ? ((currentPrice - lastPrice) / lastPrice * 100) : 0;

                return (
                  <React.Fragment key={ing.id}>
                    <tr>
                      <td className="font-medium text-[#2A2A2A]">{ing.name}</td>
                      <td className="text-[#2A2A2A]/60 text-sm">{ing.vendor || '-'}</td>
                      <td><span className="pizza-tag pizza-tag-yellow text-xs">{ing.category}</span></td>
                      <td className="font-medium">${currentPrice.toFixed(2)}</td>
                      <td>
                        <button
                          onClick={() => setPriceHistoryId(priceHistoryId === ing.id ? null : ing.id)}
                          className="flex items-center gap-1 text-xs"
                          data-testid={`price-trend-${ing.id}`}
                        >
                          {trend === 'up' && <TrendingUp className="w-3.5 h-3.5 text-[#E23D28]" />}
                          {trend === 'down' && <TrendingDown className="w-3.5 h-3.5 text-[#2E7D32]" />}
                          {trend === 'stable' && <Minus className="w-3.5 h-3.5 text-[#2A2A2A]/40" />}
                          {hasHistory && (
                            <span className={`font-medium ${trend === 'up' ? 'text-[#E23D28]' : trend === 'down' ? 'text-[#2E7D32]' : 'text-[#2A2A2A]/40'}`}>
                              {pctChange > 0 ? '+' : ''}{pctChange.toFixed(1)}%
                            </span>
                          )}
                          {!hasHistory && <span className="text-[#2A2A2A]/30">-</span>}
                        </button>
                      </td>
                      <td>{ing.purchaseQuantity}</td>
                      <td>{ing.purchaseUnit}</td>
                      <td>{ing.yieldPercent}%</td>
                      <td className="font-bold text-[#E23D28]">{formatCurrency(ing.costPerUsableUnit)}</td>
                      <td>
                        <div className="flex gap-1">
                          <button onClick={() => setPriceHistoryId(priceHistoryId === ing.id ? null : ing.id)} className="p-1.5 hover:bg-[#FFC107]/20 rounded" data-testid={`history-ingredient-${ing.id}`} title="Price History">
                            <History className="w-4 h-4 text-[#2A2A2A]/50" />
                          </button>
                          <button onClick={() => startEdit(ing)} className="p-1.5 hover:bg-[#FFC107]/20 rounded" data-testid={`edit-ingredient-${ing.id}`}>
                            <Edit2 className="w-4 h-4 text-[#2A2A2A]" />
                          </button>
                          <button onClick={() => deleteIngredient(ing.id)} className="p-1.5 hover:bg-red-100 rounded" data-testid={`delete-ingredient-${ing.id}`}>
                            <Trash2 className="w-4 h-4 text-[#E23D28]" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Price history expansion */}
                    {priceHistoryId === ing.id && (
                      <tr>
                        <td colSpan="10" className="bg-[#FFF8E7] p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <History className="w-4 h-4 text-[#2A2A2A]" />
                            <h4 className="text-lg text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>
                              Price History — {ing.name}
                            </h4>
                          </div>
                          {history.length >= 1 ? (
                            <div className="grid md:grid-cols-2 gap-4">
                              {/* Chart */}
                              <div className="bg-white rounded-lg border border-[#2A2A2A]/10 p-3" data-testid={`price-chart-${ing.id}`}>
                                <ResponsiveContainer width="100%" height={160}>
                                  <LineChart data={history}>
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                    <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${v}`} domain={['auto', 'auto']} />
                                    <Tooltip formatter={v => [`$${Number(v).toFixed(2)}`, 'Cost']} />
                                    <Line type="monotone" dataKey="cost" stroke="#E23D28" strokeWidth={2} dot={{ fill: '#E23D28', r: 4 }} />
                                  </LineChart>
                                </ResponsiveContainer>
                              </div>
                              {/* Log */}
                              <div className="space-y-1">
                                <p className="text-xs font-semibold text-[#2A2A2A]/60 mb-1" style={{ fontFamily: "'Bebas Neue', cursive" }}>Price Log</p>
                                {[...history].reverse().map((h, i) => {
                                  const prevEntry = history[history.length - 1 - i - 1];
                                  const change = prevEntry ? h.cost - prevEntry.cost : 0;
                                  return (
                                    <div key={i} className="flex items-center justify-between text-sm px-2 py-1 bg-white rounded border border-[#2A2A2A]/5">
                                      <span className="text-[#2A2A2A]/60 text-xs">{h.date}</span>
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">${h.cost.toFixed(2)}</span>
                                        {change !== 0 && (
                                          <span className={`text-xs font-medium ${change > 0 ? 'text-[#E23D28]' : 'text-[#2E7D32]'}`}>
                                            {change > 0 ? '+' : ''}{change.toFixed(2)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                                {history.length === 1 && (
                                  <p className="text-xs text-[#2A2A2A]/40 mt-2 italic">
                                    Update this ingredient's cost on different days to see price trends over time.
                                  </p>
                                )}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-[#2A2A2A]/50">
                              No price data yet. Add or update this ingredient to start tracking.
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="pizza-card p-8 text-center" data-testid="ingredients-empty">
          <Database className="w-12 h-12 text-[#2A2A2A]/20 mx-auto mb-3" />
          <h3 className="text-xl text-[#2A2A2A] mb-1" style={{ fontFamily: "'Bebas Neue', cursive" }}>No Ingredients Yet</h3>
          <p className="text-sm text-[#2A2A2A]/50 mb-4">Add ingredients manually or upload a CSV/XLSX file.</p>
          <button onClick={() => { setShowForm(true); setEditId(null); setForm({ ...EMPTY_INGREDIENT }); }}
            className="pizza-btn text-sm" data-testid="empty-add-ingredient-btn">
            <Plus className="w-4 h-4 inline mr-1" /> Add First Ingredient
          </button>
        </div>
      )}
    </div>
  );
}
